import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Copy, Check, Send, Download, Users, ArrowRight, Info, Loader2, Wallet, Landmark } from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "react-toastify";
import { useReferralCode } from "../hooks/useReferralCode";
import headerlogo from "../assets/headerLogo.svg"; // same import as HeaderShell
import BankDetailsCard from "../components/BankDetailsCard"

const BRAND = "#5D45F9";
const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Shared width for the QR card and the Invite / Download buttons only
const NARROW = "max-w-[310px] mx-auto";

// Logo block at the top of the downloaded card
const LOGO_H = 30;
const LOGO_TOP = 20;
const LOGO_SHIFT = 38; // how far everything below moves down to make room for the logo

// Rounded-rect path helper (works in browsers without ctx.roundRect)
const roundedRectPath = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// Trim text with an ellipsis so it fits within maxWidth (uses current ctx.font)
const fitText = (ctx, text, maxWidth) => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) {
    t = t.slice(0, -1);
  }
  return `${t.trimEnd()}…`;
};

// Load an image from a URL / blob URL
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const ReferAndEarnContent = ({ token, workerType }) => {
  const navigate = useNavigate();

  const { loading, error, referralCode, referrerName, amount, referralLink, howItWorks } =
    useReferralCode({ token, workerType });

  const [copied, setCopied] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const qrFileRef = useRef(null);
  const copyTimerRef = useRef(null);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  // Copy code → temporary "Copied!" tooltip (no toast)
  const handleCopyCode = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      toast.error("Couldn't copy code");
    }
  };

  // Draws the full card (logo + title + QR + referral name + code) and returns the canvas
  const renderCardCanvas = async () => {
    const svg = document.getElementById("referral-qr-svg");
    if (!svg) throw new Error("QR not found");

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const qrUrl = URL.createObjectURL(svgBlob);

    try {
      // Load the QR and the Factrika logo together
      const [img, logoImg] = await Promise.all([loadImage(qrUrl), loadImage(headerlogo)]);

      const W = 340;
      const H = (referrerName ? 412 : 380) + LOGO_SHIFT;
      const SCALE = 3; // high-res output

      const canvas = document.createElement("canvas");
      canvas.width = W * SCALE;
      canvas.height = H * SCALE;
      const ctx = canvas.getContext("2d");
      ctx.scale(SCALE, SCALE);

      // Card background
      roundedRectPath(ctx, 0, 0, W, H, 20);
      ctx.fillStyle = "#E7E5FD";
      ctx.fill();

      // Factrika logo (centered, aspect ratio preserved)
      const ratio =
        logoImg.naturalWidth && logoImg.naturalHeight
          ? logoImg.naturalWidth / logoImg.naturalHeight
          : 4; // fallback if the SVG has no intrinsic size
      let logoH = LOGO_H;
      let logoW = logoH * ratio;
      const maxLogoW = W - 80;
      if (logoW > maxLogoW) {
        logoW = maxLogoW;
        logoH = logoW / ratio;
      }
      ctx.drawImage(logoImg, (W - logoW) / 2, LOGO_TOP + (LOGO_H - logoH) / 2, logoW, logoH);

      // Title
      ctx.fillStyle = "#374151";
      ctx.font = `600 16px ${FONT_STACK}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText("Scan Referral QR", W / 2, 42 + LOGO_SHIFT);

      // White box behind QR
      const boxSize = 252;
      const boxX = (W - boxSize) / 2;
      const boxY = 62 + LOGO_SHIFT;
      roundedRectPath(ctx, boxX, boxY, boxSize, boxSize, 12);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();

      // QR code
      const qrSize = 228;
      ctx.drawImage(img, boxX + (boxSize - qrSize) / 2, boxY + (boxSize - qrSize) / 2, qrSize, qrSize);

      let cursorY = boxY + boxSize + 34;

      // Referral name
      if (referrerName) {
        ctx.fillStyle = "#111827";
        ctx.font = `700 17px ${FONT_STACK}`;
        ctx.textAlign = "center";
        ctx.fillText(fitText(ctx, referrerName, W - 40), W / 2, cursorY);
        cursorY += 32;
      }

      // Footer: "Referral code :  <code>" — centered by measured width
      const label = "Referral code :";
      const labelFont = `500 15px ${FONT_STACK}`;
      const codeFont = `700 16px ${FONT_STACK}`;
      const gap = 8;

      ctx.textAlign = "left";
      ctx.font = labelFont;
      const labelW = ctx.measureText(label).width;
      ctx.font = codeFont;
      const codeW = ctx.measureText(referralCode).width;

      const startX = (W - (labelW + gap + codeW)) / 2;

      ctx.fillStyle = "#374151";
      ctx.font = labelFont;
      ctx.fillText(label, startX, cursorY);

      ctx.fillStyle = BRAND;
      ctx.font = codeFont;
      ctx.fillText(referralCode, startX + labelW + gap, cursorY);

      return canvas;
    } finally {
      URL.revokeObjectURL(qrUrl);
    }
  };

  // Canvas → PNG File (needed for sharing the QR image)
  const canvasToFile = (canvas) =>
    new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Couldn't create image"));
        resolve(new File([blob], `factrika-referral-${referralCode}.png`, { type: "image/png" }));
      }, "image/png");
    });

  // Pre-build the QR card image so sharing stays inside the tap gesture
  // (some browsers block navigator.share if async work happens first)
  useEffect(() => {
    if (loading || !referralCode || !referralLink) return;
    let cancelled = false;
    qrFileRef.current = null;
    (async () => {
      try {
        const file = await canvasToFile(await renderCardCanvas());
        if (!cancelled) qrFileRef.current = file;
      } catch {
        // falls back to building on demand when the user taps share
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, referralCode, referralLink, referrerName]);

  const isRNWebView = () =>
    typeof window !== "undefined" &&
    window.ReactNativeWebView &&
    typeof window.ReactNativeWebView.postMessage === "function";

  // Convert a File to base64 (no data: prefix) for the RN bridge
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        // strip "data:image/png;base64,"
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Share via React Native's native Android share sheet
  const shareViaReactNative = async ({ message, file, mimeType }) => {
    const payload = {
      type: "SHARE_REFERRAL",
      message,
      mimeType: mimeType || "image/png",
    };

    if (file) {
      try {
        const b64 = await fileToBase64(file);
        // Only attach if we actually got real data
        if (b64 && b64.length > 0) {
          payload.base64 = b64;
        }
      } catch (e) {
        console.warn("Could not encode QR image for RN share:", e);
      }
    }

    window.ReactNativeWebView.postMessage(JSON.stringify(payload));
  };

  // Ask React Native to save the QR image to the gallery / files
  const downloadViaReactNative = ({ file, filename, mimeType }) =>
  new Promise((resolve) => {
    let timer;
    const onResult = (e) => {
      cleanup();
      resolve(e.detail || { status: "error", message: "Couldn't save QR" });
    };
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("referral-download-result", onResult);
    };

    window.addEventListener("referral-download-result", onResult);
    // long timeout: the user may take a while on the permission dialog
    timer = setTimeout(() => {
      cleanup();
      resolve({ status: "error", message: "Couldn't save QR" });
    }, 60000);

    (async () => {
      const payload = {
        type: "DOWNLOAD_REFERRAL",
        filename,
        mimeType: mimeType || "image/png",
      };
      if (file) {
        try {
          const b64 = await fileToBase64(file);
          if (b64 && b64.length > 0) payload.base64 = b64;
        } catch (e) {
          console.warn("Could not encode QR image for RN download:", e);
        }
      }
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    })();
  });

  // Share: message with referral code + link, plus the QR card image when supported
  const handleInvite = async () => {
    if (!referralCode) return;

    const baseText = [
      "Join me on Factrika and start earning! 💼",
      "",
      `Scan the QR code`,
      `Or sign up using this link: ${referralLink}`,
    ].join("\n");
    const textWithQr = `${baseText}\n\nYou can also use my referral code: ${referralCode} to get started.`;

    // 1) React Native WebView → native Android share sheet (with QR image if available)
    if (isRNWebView()) {
      let file = qrFileRef.current;
      if (!file) {
        file = await canvasToFile(await renderCardCanvas()).catch(() => null);
      }
      await shareViaReactNative({
        message: file ? textWithQr : baseText,
        file,
        mimeType: "image/png",
      });
      return;
    }

    // 2) Regular browser: Web Share API (image + text, then text-only)
    try {
      let file = qrFileRef.current;
      if (!file) {
        file = await canvasToFile(await renderCardCanvas()).catch(() => null);
      }

      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: "Join Factrika", text: textWithQr, files: [file] });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: "Join Factrika", text: baseText });
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
    }

    // 3) Desktop fallback
    try {
      await navigator.clipboard.writeText(baseText);
      toast.success("Referral message copied — share it with your friends!");
    } catch {
      toast.error("Couldn't share referral");
    }
  };

  // Downloads the full card as a PNG
  //  - inside React Native WebView → hands the image to the app to save
  //  - in a normal browser         → standard anchor download
  const handleDownloadQR = async () => {
    if (!referralCode || downloading) return;
    setDownloading(true);
    try {
      const file = await canvasToFile(await renderCardCanvas());
      const filename = `factrika-referral-${referralCode}.png`;

      // 1) React Native WebView → native gallery / files save
      if (isRNWebView()) {
  const result = await downloadViaReactNative({ file, filename, mimeType: "image/png" });
  if (result.status === "success") {
    toast.success("QR saved to your gallery");
  } else {
    toast.error(result.message || "Couldn't save QR");
  }
  return;
}

      // 2) Regular browser → standard download
      const canvas = await renderCardCanvas();
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download QR:", err);
      toast.error("Couldn't download QR");
    } finally {
      setDownloading(false);
    }
  };

  const goToMyReferralsList = () => {
    const path = workerType === "admin"
      ? "/lkart/admin/my-referrals"
      : "/lkart/dashboard/my-referrals";
    navigate(path);
  };

  // Navigate to Referral Payout page (workers only)
  const goToReferralPayout = () => {
    navigate("/lkart/dashboard/referral-payout");
  };

  // ─── Loading state ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-[#5D45F9] animate-spin" />
      </div>
    );
  }

  // ─── Error / no data state ─────────────────────────────────────
  if (error || !referralCode) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-3.5 gap-2">
        <p className="text-gray-600 text-xs text-center">
          {error || "Couldn't load your referral code"}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="text-[#5D45F9] font-semibold text-xs"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafc] pb-8 text-gray-900 font-sans">
      {/* Spacer where the FACTRIKA header used to be — spacing kept, no content */}
      <div className="py-5" />

      <div className="px-3.5 pt-5 max-w-md mx-auto">
        {/* Back button + REFER AND EARN title */}
        <div className="flex items-center justify-center gap-2 mb-4 relative">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="absolute left-0 w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-900" />
          </button>
          <p className="text-center text-[11px] font-bold tracking-[0.15em] text-[#5D45F9]">
            REFER AND EARN
          </p>
        </div>

        <h1 className="text-center text-base leading-tight font-extrabold text-gray-900 mb-4">
          Invite Your Friends
          <br />
          and{" "}
          <span className="text-[#5D45F9]">{amount ? amount : "Get ₹100"}</span>
        </h1>

        {/* QR Card — narrower, centered */}
        <div className={`bg-[#E7E5FD] rounded-xl p-4 mb-4 ${NARROW}`}>
          <p className="text-center text-xs font-semibold text-gray-700 mb-3">
            Scan Referral QR
          </p>
          <div className="flex items-center justify-center mb-3">
            <div className="bg-white p-2.5 rounded-lg">
              <QRCode id="referral-qr-svg" value={referralLink} size={120} />
            </div>
          </div>

          <div className="flex items-center justify-center gap-1.5 text-xs">
            <span className="text-gray-700">Referral code :</span>
            <span className="font-bold text-[#5D45F9]">{referralCode}</span>
            <div className="relative">
              <button
                onClick={handleCopyCode}
                className="text-[#5D45F9] hover:opacity-80 transition-opacity"
                title="Copy referral code"
                aria-label="Copy referral code"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
              {copied && (
                <span
                  role="status"
                  className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-medium px-2 py-1 rounded shadow-md whitespace-nowrap"
                >
                  Copied!
                  <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons — same width as QR card, text never wraps */}
        <div className={`grid grid-cols-2 gap-2.5 mb-5 ${NARROW}`}>
          <button
            onClick={handleInvite}
            className="min-h-[40px] py-2 px-2 flex items-center justify-center gap-1.5 whitespace-nowrap bg-[#5D45F9] hover:bg-[#4d37e2] text-white font-semibold text-xs rounded-lg transition-colors"
          >
            <Send className="w-3.5 h-3.5 shrink-0" />
            <span>Invite Your Friends</span>
          </button>
          <button
            onClick={handleDownloadQR}
            disabled={downloading}
            className="min-h-[40px] py-2 px-2 flex items-center justify-center gap-1.5 whitespace-nowrap bg-[#5D45F9] hover:bg-[#4d37e2] disabled:opacity-70 text-white font-semibold text-xs rounded-lg transition-colors"
          >
            <Download className="w-3.5 h-3.5 shrink-0" />
            <span>Download QR</span>
          </button>
        </div>

        {/* How it works — fully backend-driven, any number of steps */}
        {howItWorks.length > 0 && (
          <>
            <div className="flex items-center gap-1.5 mb-3">
              <Info className="w-3.5 h-3.5 text-[#5D45F9]" />
              <p className="text-[#5D45F9] font-semibold text-xs">How it works</p>
            </div>

            <div className="mb-5">
              {howItWorks.map((step, index) => (
                <div key={`${step.title}-${index}`} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full bg-white border-2 border-[#5D45F9] text-[#5D45F9] font-bold flex items-center justify-center text-xs flex-shrink-0">
                      {index + 1}
                    </div>
                    {index < howItWorks.length - 1 && (
                      <div className="w-0.5 flex-1 border-l-2 border-dotted border-indigo-200 my-1" />
                    )}
                  </div>
                  <div className={index < howItWorks.length - 1 ? "pb-4" : ""}>
                    {step.title && (
                      <p className="font-bold text-gray-900 text-xs mb-0.5">{step.title}</p>
                    )}
                    {step.description && (
                      <p className="text-gray-500 text-[11px] leading-relaxed">{step.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* My Referrals CTA — original full width */}
        <button
          onClick={goToMyReferralsList}
          className="w-full flex items-center justify-center gap-1.5 bg-[#5D45F9] hover:bg-[#4d37e2] text-white font-semibold text-sm py-3 rounded-lg transition-colors"
        >
          <Users className="w-4 h-4" />
          My Referrals
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
  onClick={() => setShowBankDetails(true)}
  className="w-full flex items-center justify-center gap-1.5 bg-white hover:bg-[#EEECFF] text-[#5D45F9] font-semibold text-sm py-3 rounded-lg border-2 border-[#5D45F9] transition-colors mt-2.5"
>
  <Landmark className="w-4 h-4" />
  Bank Details
  <ArrowRight className="w-3.5 h-3.5" />
</button>

        {/* Referral Payout button — visible only for workers (not admin) */}
        {workerType !== "admin" && (
          <button
            onClick={goToReferralPayout}
            className="w-full flex items-center justify-center gap-1.5 bg-white hover:bg-[#EEECFF] text-[#5D45F9] font-semibold text-sm py-3 rounded-lg border-2 border-[#5D45F9] transition-colors mt-2.5"
          >
            <Wallet className="w-4 h-4" />
            Referral Payout
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
       <BankDetailsCard
        isOpen={showBankDetails}
        onClose={() => setShowBankDetails(false)}
      />
    </div>
  );
};

export default ReferAndEarnContent;